// useCustomQuery.js
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useMemo } from "react";
import OrderService from "@services/OrderService";
import UserServices from "@services/UserService";
import TradeService from "@services/Trade";
import TransactionServices from "@services/TransactionServices";
import WalletServices from "@services/Mywallet";
import ProfileServices from "@services/ProfileServices";
import NotificationServices from "@services/NotificationServices";
import NewsServices from "@services/News";
import FinancialServices from "@services/FinancialServices";
import operations from "../../services/Mywallet";
import axios from "@configs/axios";
import { createOptimisticMutation, OPTIMISTIC_PATTERNS } from "../../utils/optimisticUpdates";
import { useWalletCacheManager } from "./useWalletCacheManager";

// Shared orders cache to prevent duplicate requests
const orderCache = new Map();

// Expose orderCache globally for cache invalidation
if (typeof window !== 'undefined') {
  window.orderCache = orderCache;
  // Global fallback for incognito mode - persists across navigation
  window._filterCoinsCache = window._filterCoinsCache || null;
}
export const useFetchOrder = (page, query, setCanncel) => {
  const onSuccess = (data) => {
    if (setCanncel) setCanncel(false);

    // 🔍 DEBUG: Log order data to see what API is returning
    console.log('📋 useFetchOrder API response:', {
      page,
      query,
      totalOrders: data?.list?.length || 0,
      orders: data?.list?.map(o => ({
        id: o.id,
        market: o.market,
        status: o.status,
        filled: o.filled,
        amount: o.amount
      })) || [],
      timestamp: new Date().toISOString()
    });
  };

  const cacheKey = `${page}-${query}`;

  return useQuery(
    ["fetchOrder", page, query],
    () => {
      console.log('🔄 useFetchOrder: Making API call', { page, query, cacheKey });

      // Check if we already have a pending request for this exact query
      if (orderCache.has(cacheKey)) {
        console.log('♻️ useFetchOrder: Using cached promise', { cacheKey });
        return orderCache.get(cacheKey);
      }

      // Create new request and cache it
      const promise = OrderService.fetchOrders(page, query)
        .finally(() => {
          // Remove from cache after completion to allow future requests
          setTimeout(() => {
            orderCache.delete(cacheKey);
            console.log('🗑️ useFetchOrder: Removed from cache', { cacheKey });
          }, 1000 * 60 * 2); // 2 minutes
        });

      orderCache.set(cacheKey, promise);
      console.log('💾 useFetchOrder: Cached new promise', { cacheKey });
      return promise;
    },
    {
      select: (data) => {
        const orderData = data.data;
        return orderData;
      },
      // Optimize cache settings to reduce redundant requests
      staleTime: 1000 * 60 * 2, // 2 minutes for orders (more dynamic data)
      cacheTime: 1000 * 60 * 10, // 10 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Don't refetch on mount if data exists
      refetchOnReconnect: false,
      keepPreviousData: true, // Keep previous data while fetching new
      onSuccess,
    }
  );
};
// Shared notifications cache to prevent duplicate requests
const notificationCache = new Map();
export const useFetchNotifications = (page, query, lang) => {
  const cacheKey = `${page}-${query}-${lang}`;

  return useQuery(
    ["fetchNotife", page, query, lang],
    () => {
      // Check if we already have a pending request for this exact query
      if (notificationCache.has(cacheKey)) {
        return notificationCache.get(cacheKey);
      }

      // Create new request and cache it
      const promise = NotificationServices.getNotificationList(page, query, lang)
        .finally(() => {
          // Remove from cache after completion to allow future requests
          setTimeout(() => notificationCache.delete(cacheKey), 1000 * 60 * 2); // 2 minutes
        });

      notificationCache.set(cacheKey, promise);
      return promise;
    },
    {
      select: (data) => {
        const orderData = data.data;
        return orderData;
      },
      // Optimize cache settings for notifications
      staleTime: 1000 * 60 * 2, // 2 minutes
      cacheTime: 1000 * 60 * 15, // 15 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Don't refetch on mount if data exists
      refetchOnReconnect: false,
      keepPreviousData: true,
    }
  );
};
export const useFetchNews = (lang) => {
  return useQuery(["fetchNews", lang], () => NewsServices.fetchNews(lang), {
    select: (data) => {
      const orderData = data.data;
      return orderData;
    },
    // Optimize cache settings for news (changes moderately)
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 30, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch on mount if data exists
    refetchOnReconnect: false,
    keepPreviousData: true,
  });
};

export const useFetchUserTopWallets = () => {
  return useQuery(["fetchUserTopWallets"], UserServices.fetchUserTopWallets, {
    // Optimize cache settings for top wallets
    staleTime: 1000 * 60 * 5, // 5 minutes
    cacheTime: 1000 * 60 * 20, // 20 minutes
    refetchOnWindowFocus: false, // Changed from true to false
    refetchOnMount: false,
    refetchOnReconnect: false,
    keepPreviousData: true,
  });
};

export const useCancelOrder = (activeOrder) => {
  return useQuery(
    ["cancelOrder", activeOrder],
    () => OrderService.cancelOrder(activeOrder),
    {
      refetchOnWindowFocus: false,
      enabled: Boolean(activeOrder),
    }
  );
};

export const useBuyInvoice = (
  activeCurrency,
  tomanAmount,
  invoice,
  walletSwitch,
  walletRadio,
  receiptType,
  activeBuyInvoice,
  setIsInvoice,
  setActiveBuyInvoice,
  setOrderInvoiceInfo
) => {
  const onSuccess = (data) => {
    setIsInvoice(true);
    setActiveBuyInvoice(false);
    setOrderInvoiceInfo(data);
  };
  return useQuery(
    [
      "buyInvoice",
      activeCurrency,
      tomanAmount,
      invoice,
      walletSwitch,
      walletRadio,
      receiptType,
    ],
    () =>
      TradeService.createMarketBuy(
        activeCurrency,
        tomanAmount,
        invoice,
        walletSwitch && walletRadio === "IRR",
        walletSwitch && walletRadio === "USDT",
        receiptType
      ),
    {
      select: (data) => {
        const buyInvoice = data.data;
        return buyInvoice;
      },
      initialData: () => {
        const data = {};
        return data;
      },
      enabled: Boolean(activeBuyInvoice),
      onSuccess,
    }
  );
};

export const useSellInvoice = (
  activeCurrency,
  tokenAmount,
  invoice,
  receiptType,
  setIsInvoice,
  setOrderInvoiceInfo,
  activeSellInvoice,
  setActiveSellInvoice,
  orderType,
  walletStore
) => {
  const onSuccess = (data) => {
    setIsInvoice(true);
    setOrderInvoiceInfo(data);
    setActiveSellInvoice(false);
  };
  return useQuery(
    ["sellInvoice", activeCurrency.symbol, tokenAmount, invoice, receiptType],
    () =>
      TradeService.createMarketSell(
        activeCurrency.symbol,
        tokenAmount,
        invoice,
        receiptType
      ),
    {
      select: (data) => {
        const sellInvoice = data.data;
        return sellInvoice;
      },
      initialData: () => {
        const data = {};
        return data;
      },
      enabled: Boolean(activeSellInvoice),
      onSuccess,
    }
  );
};

export const useBuySellInvoice = (
  orderType,
  setIsInvoice,
  setActiveInvoice,
  activeCurrency,
  tomanAmount,
  invoice,
  walletSwitch,
  walletRadio,
  receiptType,
  activeInvoice,
  tokenAmount,
  max
) => {
  if (orderType === "buy") {
    const onSuccess = (data) => {
      setIsInvoice(true);
      setActiveInvoice(false);
    };
    return useQuery(
      [
        "buyInvoice",
        activeCurrency,
        tomanAmount,
        invoice,
        walletSwitch,
        walletRadio,
        receiptType,
      ],
      () =>
        TradeService.createMarketBuy(
          activeCurrency,
          tomanAmount,
          invoice,
          walletSwitch && walletRadio === "IRR",
          walletSwitch && walletRadio === "USDT",
          receiptType
        ),
      {
        select: (data) => {
          const buyInvoice = data.data;
          return buyInvoice;
        },
        initialData: () => {
          const data = {};
          return data;
        },
        enabled: Boolean(activeInvoice) || Boolean(walletSwitch),
        onSuccess,
      }
    );
  } else if (orderType === "sell") {
    if (tokenAmount !== 0 && tokenAmount <= max) {
      const onSuccess = (data) => {
        setIsInvoice(true);
        setActiveInvoice(false);
      };
      return useQuery(
        ["sellInvoice", activeCurrency.symbol, tokenAmount, invoice, receiptType],
        () =>
          TradeService.createMarketSell(
            activeCurrency.symbol,
            tokenAmount,
            invoice,
            receiptType
          ),
        {
          select: (data) => {
            const sellInvoice = data.data;
            return sellInvoice;
          },
          initialData: () => {
            const data = {};
            return data;
          },
          enabled: Boolean(activeInvoice) || Boolean(walletSwitch),
          onSuccess,
        }
      );
    } else {
      return {};
    }
  }

  return {};
};

export const useFetchOrderCalculator = (page, query) => {
  return useQuery(["orderCalculator"], () => OrderService.orderCalculator(), {
    select: (res) => {
      const calculateData = res.data.list;

      return calculateData;
    },
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    // keepPreviousData: true,
  });
};

// WalletServices is already imported on line 7

export const useFetchWallet = (options = {}) => {
  const { invalidateOnError } = useWalletCacheManager();

  // Determine if this is a wallet-related page that should refresh on focus
  const isWalletPage = window.location.pathname.includes('/myWallet') ||
                       window.location.pathname.includes('/wallet') ||
                       window.location.pathname.includes('/trade') ||
                       window.location.pathname.includes('/order');

  const queryResult = useQuery(["fetchWallet"], () => WalletServices.fetchWalletList(), {
    // Balanced cache settings - less aggressive for better UX
    staleTime: 1000 * 60 * 3, // 3 minutes - reduced from 10 minutes
    cacheTime: 1000 * 60 * 15, // 15 minutes - reduced from 30 minutes
    refetchOnWindowFocus: isWalletPage, // Enable for wallet pages
    refetchOnMount: false, // Don't refetch on component mount if data exists
    refetchOnReconnect: true, // Enable reconnect refetch for better reliability
    onError: (error) => {
      // Skip error handling if this is a token expiration error
      if (error._handledByTokenExpiration || error?.status === 401) {
        console.log('✅ Wallet fetch: Skipping error handling - token expiration in progress');
        return;
      }

      // For network errors (API down, connection issues), don't show toast
      // This is a background refetch - follow UX patterns from major crypto exchanges
      const isNetworkError = error?.status === 0 || !error?.response;
      if (isNetworkError) {
        console.log('🔇 Wallet fetch: Suppressing network error toast for background refetch');
        return;
      }

      console.error('❌ Wallet fetch error:', error);
      // Trigger error recovery after a delay
      setTimeout(() => {
        invalidateOnError(error, 'wallet_fetch_error');
      }, 5000);
    },
    ...options, // Allow overriding options when calling the hook
  });

  // Enhanced refetch function that logs for debugging
  const enhancedRefetch = (...args) => {
    console.log('🔄 Legacy refetchWallet called - consider using useWalletCacheManager');
    return queryResult.refetch(...args);
  };

  return {
    ...queryResult,
    refetch: enhancedRefetch
  };
};
export const useFetchWalletSummary = (options = {}) => {
  const { invalidateOnError } = useWalletCacheManager();

  // Determine if this is a wallet-related page
  const isWalletPage = window.location.pathname.includes('/myWallet') ||
                       window.location.pathname.includes('/wallet');

  return useQuery(
    ["fetchWalletSummary"],
    () => WalletServices.getMyWalletList().then(data => data.summary),
    {
      // Balanced cache settings
      staleTime: 1000 * 60 * 3, // 3 minutes - reduced from 10 minutes
      cacheTime: 1000 * 60 * 15, // 15 minutes - reduced from 30 minutes
      refetchOnWindowFocus: isWalletPage, // Enable for wallet pages
      refetchOnMount: false, // Don't refetch on component mount if data exists
      refetchOnReconnect: true, // Enable reconnect refetch
      onError: (error) => {
        // Skip error handling if this is a token expiration error
        if (error._handledByTokenExpiration || error?.status === 401) {
          console.log('✅ Wallet summary: Skipping error handling - token expiration in progress');
          return;
        }

        // For network errors (API down, connection issues), don't show toast
        // This is a background refetch - follow UX patterns from major crypto exchanges
        const isNetworkError = error?.status === 0 || !error?.response;
        if (isNetworkError) {
          console.log('🔇 Wallet summary: Suppressing network error toast for background refetch');
          return;
        }

        console.error('❌ Wallet summary fetch error:', error);
        setTimeout(() => {
          invalidateOnError(error, 'wallet_summary_fetch_error');
        }, 5000);
      },
      ...options, // Allow overriding options when calling the hook
    }
  );
};

const getMarkets = async () => {
  return await axios.get("/markets");
};

export const useGetMarkets = () => {
  return useQuery(["getMarkets"], () => getMarkets(), {
    select: (res) => {
      const marketsData = res.data;

      // Since we're using the same /markets endpoint, just filter by status directly
      const filteredMarkets = marketsData?.data?.filter(market => {
        const isActive = market.status === "Active";

        // Reduce spam by only logging inactive markets occasionally
        if (!isActive && Math.random() < 0.1) { // Only log 10% of inactive markets
          console.log(`🚫 useGetMarkets: Filtering out inactive market: ${market.market_name}`);
        }

        return isActive;
      });

      // Only log market filtering when the count changes or every 30 seconds to reduce spam
      const currentCount = filteredMarkets?.length || 0;
      const totalCount = marketsData?.data?.length || 0;
      const now = Date.now();

      if (!window._lastMarketFilterLog ||
          window._lastMarketFilterLog.count !== currentCount ||
          window._lastMarketFilterLog.total !== totalCount ||
          now - window._lastMarketFilterLog.timestamp > 30000) {

        console.log(`🔍 useGetMarkets: Filtered markets: ${currentCount}/${totalCount} markets shown`);
        window._lastMarketFilterLog = { count: currentCount, total: totalCount, timestamp: now };
      }

      return {
        ...marketsData,
        data: filteredMarkets || []
      };
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    // keepPreviousData: true,
  });
};

const getCoins = async () => {
  return await axios({
    method: "GET",
    url: `${process.env.REACT_APP_IRAN_WEBSOCKET_URL}/api/bit/order/v1/index/price`,
    withCredentials: false,
  });
};

export const useGetCoins = () => {
  return useQuery(["getCoinsApi"], () => getCoins(), {
    select: (res) => {
      return res;
    },
    // Optimize cache settings for coin prices
    staleTime: 1000 * 30, // 30 seconds for price data (more dynamic)
    cacheTime: 1000 * 60 * 5, // 2 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    keepPreviousData: true,
  });
};

export const useFetchCodesList = (page, query) => {
  return useQuery(
    ["codesList", page, query],
    () => UserServices.fetchUserReferralsCodesList(page, query),
    {
      select: (data) => {
        const codeList = data.data;

        return codeList;
      },

      refetchOnWindowFocus: false,
      refetchOnMount: true,
      //   keepPreviousData: true,
    }
  );
};

export const useFetchInvitedUserList = (page, query) => {
  return useQuery(
    ["invitedUserList", page, query],
    () => UserServices.fetchUserReferralsInvitedUserList(page, query),
    {
      select: (res) => {
        const inviteUserList = res.data;
        return inviteUserList;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      //   keepPreviousData: true,
    }
  );
};

export const useFetchUserReferralsData = (page, query) => {
  return useQuery(
    ["fetchUserReferralsData"],
    () => UserServices.fetchUserReferralsData(),
    {
      select: (res) => {
        const fetchUserReferralsData = res.data;
        return fetchUserReferralsData;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      //   keepPreviousData: true,
    }
  );
};

export const useFetchUserReferralIncome = (page, query) => {
  return useQuery(
    ["fetchUserReferralIncome"],
    () => UserServices.fetchUserReferralIncome(),
    {
      select: (res) => {
        const fetchUserReferralIncome = res.data.total_income;
        return fetchUserReferralIncome;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      //   keepPreviousData: true,
    }
  );
};

export const useCreateUserReferralsCode = (
  setCreateReferralModalShow,
  createReferralModalShow,
  commissionPercent,
  active,
  refetch,
  setIsCreateLoading,
  setActive
) => {
  const onSuccess = (res) => {
    if (res.status === 1 && res.code === 200) {
      setCreateReferralModalShow(!createReferralModalShow);
      setActive(false);
      refetch();
      setIsCreateLoading(false);
    }
  };
  return useQuery(
    ["createUserReferralsCode", commissionPercent],
    () => UserServices.createUserReferralsCode(100 - commissionPercent),
    {
      onSuccess,
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      //   keepPreviousData: true,
      enabled: Boolean(active),
    }
  );
};

export const useFetchTransaction = (page, query) => {
  return useQuery(
    ["fetchTransaction", page, query],
    () => TransactionServices.getTransactions(page, 1, query),
    {
      select: (res) => {
        const fetchTransaction = res.data;
        return fetchTransaction;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      //   keepPreviousData: true,
    }
  );
};

export const useFetchShetabTransaction = (page, transactionType, query) => {
  return useQuery(
    ["fetchShetabTransaction", page, transactionType, query],
    () => TransactionServices.getShetabTransactions(page, transactionType, query),
    {
      select: (res) => {
        const fetchTransaction = res.data;
        return fetchTransaction;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      //   keepPreviousData: true,
    }
  );
};

export const useShetabDeposit = (value, amount, active, setActive, reserve_payment) => {
  const onSuccess = (res) => {
    if (res.code === 200 && res.data.payment_url) {
      window.open(res.data.payment_url, "_self");
      setActive(0);
      toast.success("درخواست شما با موفقیت ارسال شد");
    }
  };
  return useQuery(
    ["shetabDeposit", value, amount],
    () => WalletServices.shetabDeposit(value, amount, reserve_payment),
    {
      onSuccess,
      enabled: Boolean(active),
    }
  );
};

export const useFetchDepositTransanction = (page, query) => {
  return useQuery(
    ["fetchDepositTransanction", page, query],
    () => WalletServices.fetchDepositTransactions(page, query),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      //   keepPreviousData: true,
    }
  );
};

export const useInternalTransfer = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (payload) => WalletServices.fetchInternalTransfer(payload),
    {
      ...createOptimisticMutation(
        queryClient,
        ['fetchWallet'],
        (wallets, variables) => {
          // Optimistically update wallet balances
          return wallets.map(wallet => {
            if (wallet.symbol === variables.from_currency) {
              return {
                ...wallet,
                balance: (parseFloat(wallet.balance) - parseFloat(variables.amount)).toString(),
                available_balance: (parseFloat(wallet.available_balance) - parseFloat(variables.amount)).toString()
              };
            }
            if (wallet.symbol === variables.to_currency) {
              return {
                ...wallet,
                balance: (parseFloat(wallet.balance) + parseFloat(variables.amount)).toString(),
                available_balance: (parseFloat(wallet.available_balance) + parseFloat(variables.amount)).toString()
              };
            }
            return wallet;
          });
        },
        {
          // Don't show toast here - let the component handle success message
          errorMessage: 'Transfer failed. Please try again.',
          onSuccessCallback: (data) => {
            console.log("Transfer Successful:", data);
          },
          onErrorCallback: (error) => {
            console.error("Transfer failed:", error);
          }
        }
      )
    }
  );
};

export const useFetchHarvestTransanction = (page, query) => {
  return useQuery(
    ["fetchHarvestTransanction", page, query],
    () => TransactionServices.getTransactions(page, 2, query),
    {
      select: (res) => {
        const harvestTransaction = res.data;
        return harvestTransaction;
      },
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      //   keepPreviousData: true,
    }
  );
};

export const useFetchWithDrawTransactions = (page, query) => {
  return useQuery(
    ["fetchWithDrawTransactions", page, query],
    () => WalletServices.fetchWithDrawTransactions(page, query),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: true,
      //   keepPreviousData: true,
    }
  );
};
export const usefetchInternalTransferHistory = (page, query) => {
  return useQuery(
    ["fetchInternalTransferHistory", page, query],
    () => WalletServices.fetchInternalTransferHistory(page, query),
    {
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      //   keepPreviousData: true,
    }
  );
};

// Shared profile hook with singleton pattern
let profilePromise = null;
export const useFetchProfile = (page, query) => {
  return useQuery(["profile"], () => {
    // Implement singleton pattern to prevent multiple simultaneous requests
    if (!profilePromise) {
      profilePromise = ProfileServices.fetchProfile()
        .finally(() => {
          // Reset promise after completion to allow future requests
          setTimeout(() => { profilePromise = null; }, 1000 * 60 * 15); // Reset after 15 minutes
        });
    }
    return profilePromise;
  }, {
    // Aggressive cache settings for profile data
    staleTime: 1000 * 60 * 15, // 15 minutes - profile data changes infrequently
    cacheTime: 1000 * 60 * 60, // 1 hour
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch on mount if data exists
    refetchOnReconnect: false,
  });
};

// Shared access status hook with singleton pattern
let accessStatusPromise = null;
export const useFetchAccessStatus = () => {
  return useQuery(["accessStatus"], () => {
    // Implement singleton pattern to prevent multiple simultaneous requests
    if (!accessStatusPromise) {
      accessStatusPromise = ProfileServices.accessStatus()
        .finally(() => {
          // Reset promise after completion to allow future requests
          setTimeout(() => { accessStatusPromise = null; }, 1000 * 60 * 10); // Reset after 10 minutes
        });
    }
    return accessStatusPromise;
  }, {
    // Aggressive cache settings for access status
    staleTime: 1000 * 60 * 10, // 10 minutes - access status changes infrequently
    cacheTime: 1000 * 60 * 30, // 30 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch on mount if data exists
    refetchOnReconnect: false,
    onError: (error) => {
      // Skip error handling if this is a token expiration error
      if (error._handledByTokenExpiration || error?.status === 401) {
        console.log('✅ Access status fetch: Skipping error handling - token expiration in progress');
        return;
      }

      // For network errors (API down, connection issues), don't show toast
      // This is a background refetch - follow UX patterns from major crypto exchanges
      const isNetworkError = error?.status === 0 || !error?.response;
      if (isNetworkError) {
        console.log('🔇 Access status fetch: Suppressing network error toast for background refetch');
        return;
      }

      // For other errors, they will be handled by the global error handler
      console.error('❌ Access status fetch error:', error);
    },
  });
};

// Shared terms and conditions hook with language support
// Cache per language to prevent showing wrong language after language switch
const termsPromiseCache = new Map();

export const useFetchTermsAndConditions = (lang) => {
  return useQuery(["termsAndConditions", lang], () => {
    // Implement per-language singleton pattern to prevent multiple simultaneous requests
    const cacheKey = lang || 'default';

    if (!termsPromiseCache.has(cacheKey)) {
      const promise = UserServices.fetchTermsAndConditions(lang)
        .finally(() => {
          // Reset promise after completion to allow future requests
          setTimeout(() => {
            termsPromiseCache.delete(cacheKey);
          }, 1000 * 60 * 60); // Reset after 1 hour
        });

      termsPromiseCache.set(cacheKey, promise);
    }

    return termsPromiseCache.get(cacheKey);
  }, {
    // Very aggressive cache for terms and conditions (rarely change)
    // But separate cache per language to handle language switching
    staleTime: 1000 * 60 * 60, // 1 hour
    cacheTime: 1000 * 60 * 60 * 24, // 24 hours
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    // Extract data, return null if user has already accepted terms (backend returns empty response)
    select: (data) => data?.data || null,
    // Don't retry on error - if terms fetch fails, just return null
    retry: false,
    // Enable this query only if lang is provided
    enabled: Boolean(lang),
  });
};

// Dashboard data prefetching hook to reduce initial load requests
export const useDashboardData = (lang) => {
  // Prefetch all dashboard data in parallel
  const walletQuery = useFetchWallet();
  const walletSummaryQuery = useFetchWalletSummary();
  const accessStatusQuery = useFetchAccessStatus();
  const profileQuery = useFetchProfile();
  const termsQuery = useFetchTermsAndConditions(lang);

  return {
    wallet: walletQuery,
    walletSummary: walletSummaryQuery,
    accessStatus: accessStatusQuery,
    profile: profileQuery,
    terms: termsQuery,
    isLoading: walletQuery.isLoading || walletSummaryQuery.isLoading || accessStatusQuery.isLoading || profileQuery.isLoading,
    isError: walletQuery.isError || walletSummaryQuery.isError || accessStatusQuery.isError || profileQuery.isError,
  };
};



//get In-Active coins list to remove from cuurent crypto coins list
export const useFetchFilterCoin = (options = {}) => {
  // Reuse the markets data from useGetMarkets to avoid duplicate API calls
  const { data: marketsData, isLoading, error } = useQuery(["getMarkets"], () => getMarkets(), {
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch on mount to prevent loading state in incognito mode
    staleTime: 1000 * 60 * 5, // 5 minutes - cache filter data longer
    cacheTime: 1000 * 60 * 15, // 15 minutes
    keepPreviousData: true, // Keep previous data while loading new data
    // Add retry logic for incognito mode
    retry: (failureCount, error) => {
      // In incognito mode, try to use cached data instead of retrying
      const cachedFilterData = localStorage.getItem('cached_filter_coins');
      if (cachedFilterData && failureCount < 2) {
        return false; // Don't retry, use cached data
      }
      return failureCount < 3; // Normal retry logic
    },
  });

  // Transform markets data to filter coin format
  const filterCoins = useMemo(() => {
    if (!marketsData?.data?.data) {
      // In incognito mode or when no data is available, try multiple fallback sources

      // Try global cache first (most reliable in incognito mode)
      if (window._filterCoinsCache && Array.isArray(window._filterCoinsCache) && window._filterCoinsCache.length > 0) {
        console.log(`🔍 useFetchFilterCoin: Using global cache filter data (${window._filterCoinsCache.length} coins) - incognito mode`);
        return window._filterCoinsCache;
      }

      // Try sessionStorage (works in incognito mode)
      let cachedData = null;
      try {
        const sessionData = sessionStorage.getItem('cached_filter_coins');
        if (sessionData) {
          cachedData = JSON.parse(sessionData);
          console.log(`🔍 useFetchFilterCoin: Using sessionStorage filter data (${cachedData.length} coins) - incognito mode`);
          // Update global cache
          window._filterCoinsCache = cachedData;
          return cachedData;
        }
      } catch (error) {
        console.warn('❌ Failed to parse sessionStorage filter data:', error);
      }

      // Try localStorage as backup
      try {
        const localData = localStorage.getItem('cached_filter_coins');
        if (localData) {
          cachedData = JSON.parse(localData);
          console.log(`🔍 useFetchFilterCoin: Using localStorage filter data (${cachedData.length} coins) - fallback`);
          // Update global cache
          window._filterCoinsCache = cachedData;
          return cachedData;
        }
      } catch (error) {
        console.warn('❌ Failed to parse localStorage filter data:', error);
      }

      // If no cache available, return empty array (will be handled by filtering logic)
      console.log('🔍 useFetchFilterCoin: No data available, returning empty array');
      return [];
    }

    const markets = marketsData.data.data; // API returns: { data: { data: [...] } }

    if (!Array.isArray(markets)) {
      console.log('❌ Markets data is not an array:', markets);
      return [];
    }

    // Extract unique coins from active markets
    const activeCoins = new Set();

    markets.forEach(market => {
      if (market.status === "Active" && market.coin) {
        activeCoins.add(market.coin.toUpperCase());
      }
    });

    // Transform to our expected format
    const result = Array.from(activeCoins).map(coin => ({
      symbol: coin,
      status: 1 // All coins in this list are active
    }));

    console.log(`🔍 useFetchFilterCoin: Extracted ${Array.from(activeCoins).length} active coins from ${markets.length} markets:`, Array.from(activeCoins));

    // Cache the result for incognito mode fallback (use all available methods)
    // Update global cache first (most reliable)
    window._filterCoinsCache = result;

    try {
      localStorage.setItem('cached_filter_coins', JSON.stringify(result));
      sessionStorage.setItem('cached_filter_coins', JSON.stringify(result));
      console.log('✅ Cached filter data to global cache, localStorage and sessionStorage');
    } catch (error) {
      // Try sessionStorage only if localStorage fails
      try {
        sessionStorage.setItem('cached_filter_coins', JSON.stringify(result));
        console.log('✅ Cached filter data to global cache and sessionStorage only (incognito mode)');
      } catch (sessionError) {
        console.log('✅ Cached filter data to global cache only (storage unavailable)');
      }
    }

    return result;
  }, [marketsData]);

  // Override loading state if we have filter coins data (from cache or API)
  const effectiveLoading = isLoading && (!filterCoins || filterCoins.length === 0);

  // Debug loading state changes
  if (isLoading !== effectiveLoading) {
    console.log('🔍 useFetchFilterCoin: Overriding loading state:', {
      originalLoading: isLoading,
      effectiveLoading,
      filterCoinsLength: filterCoins?.length || 0,
      hasMarketsData: !!marketsData?.data?.data
    });
  }

  return {
    data: filterCoins,
    isLoading: effectiveLoading, // Use effective loading state
    error,
    ...options
  };
};
export const useManualDepositReial = () => {
  return useQuery(["DepositReialHistory"], () => operations.manualDepositReialHistory(), {
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    // keepPreviousData: true,
  });
};
export const useGetDepositTransactionIdTips = () => {
  return useQuery(
    ["deposit-tips"],
    () => TransactionServices.getDepositTransactionIdTips(),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Don't refetch on every mount
      staleTime: 1000 * 60 * 30, // Consider data fresh for 30 minutes
      cacheTime: 1000 * 60 * 60 * 24, // Keep in cache for 24 hours
      keepPreviousData: true, // Keep previous data while fetching new
      retry: 3, // Retry failed requests 3 times
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
    }
  );
};
export const useGetDepositShetabTips = () => {
  return useQuery(["shetab-tips"], () => TransactionServices.getDepositShetabTips(), {
    refetchOnWindowFocus: false,
    refetchOnMount: false, // Don't refetch on every mount
    staleTime: 1000 * 60 * 30, // Consider data fresh for 30 minutes
    cacheTime: 1000 * 60 * 60 * 24, // Keep in cache for 24 hours
    keepPreviousData: true, // Keep previous data while fetching new
    retry: 3, // Retry failed requests 3 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });
};

/**
 * Fetch notifications with React Query caching
 * Uses stale-while-revalidate for instant UX after first load
 *
 * @param {string} lang - Language code ('en' or 'fa')
 * @returns {object} React Query result with notifications data
 */
export const useNotifications = (lang) => {
  return useQuery(
    ["notifications", lang],
    () => NotificationServices.getNotificationList(1, '', lang),
    {
      select: (data) => data.data.data,
      // Optimize for instant UX with stale-while-revalidate
      staleTime: 1000 * 60 * 2,        // 2 minutes - data considered fresh
      cacheTime: 1000 * 60 * 15,       // 15 minutes - keep in cache
      refetchOnWindowFocus: false,
      refetchOnMount: false,           // Don't refetch if cached data exists
      refetchOnReconnect: false,
      keepPreviousData: true,          // ⭐ KEY: Show old data while fetching new
      // ⚡ OPTIMIZED: Smart auto-refresh that doesn't block UI
      refetchInterval: (data) => {
        // Only auto-refresh when page is visible
        if (typeof document !== 'undefined' && document.hidden) return false;
        return 1000 * 60 * 3; // 3 minutes when visible
      },
      refetchIntervalInBackground: false, // Don't refresh when tab is hidden
      retry: 2,                        // Retry failed requests
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      onError: (error) => {
        // Skip error handling if this is a token expiration error
        if (error._handledByTokenExpiration || error?.status === 401) {
          console.log('✅ Notifications fetch: Skipping error handling - token expiration in progress');
          return;
        }

        // For network errors (API down, connection issues), don't show toast
        // This is a background refetch - follow UX patterns from major crypto exchanges
        const isNetworkError = error?.status === 0 || !error?.response;
        if (isNetworkError) {
          console.log('🔇 Notifications fetch: Suppressing network error toast for background refetch');
          return;
        }

        console.error('❌ Notifications fetch error:', error);
      },
    }
  );
};

/**
 * Fetch unread notifications with React Query caching
 * Uses stale-while-revalidate for instant UX after first load
 *
 * @param {string} lang - Language code ('en' or 'fa')
 * @returns {object} React Query result with unread notifications data
 */
export const useUnreadNotifications = (lang) => {
  return useQuery(
    ["unreadNotifications", lang],
    () => NotificationServices.getUnReadNotificationList(lang),
    {
      select: (data) => data.data.data,
      // Optimize for instant UX with stale-while-revalidate
      staleTime: 1000 * 60 * 2,        // 2 minutes - data considered fresh
      cacheTime: 1000 * 60 * 15,       // 15 minutes - keep in cache
      refetchOnWindowFocus: false,
      refetchOnMount: false,           // Don't refetch if cached data exists
      refetchOnReconnect: false,
      keepPreviousData: true,          // ⭐ KEY: Show old data while fetching new
      // ⚡ OPTIMIZED: Smart auto-refresh that doesn't block UI
      refetchInterval: (data) => {
        // Only auto-refresh when page is visible
        if (typeof document !== 'undefined' && document.hidden) return false;
        return 1000 * 60 * 3; // 3 minutes when visible
      },
      refetchIntervalInBackground: false, // Don't refresh when tab is hidden
      retry: 2,                        // Retry failed requests
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      onError: (error) => {
        // Skip error handling if this is a token expiration error
        if (error._handledByTokenExpiration || error?.status === 401) {
          console.log('✅ Unread notifications fetch: Skipping error handling - token expiration in progress');
          return;
        }

        // For network errors (API down, connection issues), don't show toast
        // This is a background refetch - follow UX patterns from major crypto exchanges
        const isNetworkError = error?.status === 0 || !error?.response;
        if (isNetworkError) {
          console.log('🔇 Unread notifications fetch: Suppressing network error toast for background refetch');
          return;
        }

        console.error('❌ Unread notifications fetch error:', error);
      },
    }
  );
};

/**
 * Fetch user financial cards (bank cards) with React Query caching
 * Used in deposit/withdrawal pages and bank card management
 *
 * @returns {object} React Query result with financial cards data
 */
export const useFetchFinancialCards = () => {
  return useQuery(
    ["financialCards"],
    () => FinancialServices.fetchUserFinancialCard(),
    {
      select: (data) => data.data,
      // Moderate cache settings - cards don't change frequently but need to be fresh
      staleTime: 1000 * 60 * 5,        // 5 minutes - data considered fresh
      cacheTime: 1000 * 60 * 30,       // 30 minutes - keep in cache
      refetchOnWindowFocus: false,
      refetchOnMount: false,           // Don't refetch if cached data exists
      refetchOnReconnect: false,
      keepPreviousData: true,          // Show old data while fetching new
      retry: 2,                        // Retry failed requests
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
    }
  );
};
